import { useAuth0 } from "@auth0/auth0-react"
import { useEffect, useReducer } from "react"
import { useSurveySparrowWebAPI } from "./useSurveySparrowWebAPI"

const initFeedbackState = {
	isLoading: true,
	error: null,
	fieldError: {
		multiChoice: false,
		textInput: false
	},
	contact: null,
	questions: null,
	response: {
		survey_id: +process.env.REACT_APP_SURVEYSPARROW_SURVEY_FEEDBACK_ID,
		contact_id: null,
		answers: []
	}
}

const feedbackReducer = (state, action) => {
	switch (action.type) {
		case "SET_LOADING":
			return {
				...state,
				isLoading: action.isLoading
			}
		case "SET_ERROR":
			return {
				...state,
				isLoading: false,
				error: action.error
			}
		case "SET_FIELDERROR":
			return {
				...state,
				fieldError: action.fieldError
			}
		case "SET_CONTACT":
			return {
				...state,
				contact: action.contact,
				response: {
					...state.response,
					contact_id: action.contact.id
				}
			}
		case "SET_QUESTIONS":
			return {
				...state,
				isLoading: false,
				questions: action.questions
			}
		case "UPDATE_ANSWER":
			return {
				...state,
				response: {
					...state.response,
					answers: state.response.answers.find((answer) => answer.question_id === action.answer.question_id)
						? state.response.answers.map((answer) => {
								if (answer.question_id === action.answer.question_id) {
									return action.answer
								}
								return answer
						  })
						: [...state.response.answers, action.answer]
				}
			}
		default:
			return state
	}
}

export const useFeedbackSurvey = () => {
	const { user } = useAuth0()
	const [state, dispatch] = useReducer(feedbackReducer, initFeedbackState)
	const { getData, postData } = useSurveySparrowWebAPI()

	const getContact = async () => {
		const response = await getData("contacts?search=" + user.email)
		if (response.ok) {
			const jsonResponse = await response.json()
			if (jsonResponse.data.length > 0) {
				const contact = jsonResponse.data.find((contact) => contact.email === user.email)
				if (contact !== null && contact !== undefined) {
					dispatch({ type: "SET_CONTACT", contact: contact })
					return true
				}
			}
		}
		return false
	}

	const createContact = async () => {
		const newContact = await postData("contacts", {
			"full_name": user.name,
			"email": user.email,
			"job_title": user.role,
			"contact_type": "contact"
		})
		try {
			if (newContact.ok) {
				const jsonResponse = await newContact.json()
				dispatch({ type: "SET_CONTACT", contact: jsonResponse.data })
			} else {
				throw new Error(newContact.message)
			}
		} catch (error) {
			dispatch({ type: "SET_ERROR", error: error.message })
		}
	}

	const getAllSurveyQuestions = async () => {
		const questions = await getData("questions?survey_id=" + process.env.REACT_APP_SURVEYSPARROW_SURVEY_FEEDBACK_ID)
		try {
			if (questions.ok) {
				const jsonResponse = await questions.json()
				if (jsonResponse.data.length === 0) {
					throw new Error("No questions found")
				}
				dispatch({ type: "SET_QUESTIONS", questions: jsonResponse.data })
			} else {
				throw new Error(questions.message)
			}
		} catch (error) {
			dispatch({ type: "SET_ERROR", error: error.message })
		}
	}

	const setResponse = (answer) => {
		dispatch({ type: "SET_FIELDERROR", fieldError: initFeedbackState.fieldError })
		dispatch({ type: "UPDATE_ANSWER", answer: answer })
	}

	const checkRequiredAnswer = () => {
		for (let index = 0; index < state.questions.length; index++) {
			const element = state.questions[index]
			if (element.is_required) {
				let answer = state.response.answers.find((answer) => answer.question_id === element.id)

				if (answer == null || answer == undefined || !answer.answer || answer.answer === "") {
					switch (element.type) {
						case "MultiChoice":
							dispatch({ type: "SET_FIELDERROR", fieldError: { multiChoice: true } })
							break
						case "TextInput":
							dispatch({ type: "SET_FIELDERROR", fieldError: { textInput: true } })
							break
						default:
							break
					}
					return false
				}
			}
		}

		dispatch({ type: "SET_FIELDERROR", fieldError: { multiChoice: false, textInput: false } })

		return true
	}

	const createResponse = async () => {
		dispatch({ type: "SET_LOADING", isLoading: true })
		const result = await postData("responses", state.response)
		try {
			if (result.ok) {
				const jsonResponse = await result.json()
				dispatch({ type: "SET_LOADING", isLoading: false })
				return jsonResponse.data.state
			} else {
				const newResponse = await result.json()
				dispatch({ type: "SET_LOADING", isLoading: false })
				throw new Error(newResponse.message)
			}
		} catch (error) {
			dispatch({ type: "SET_ERROR", error: error.message })
		}
	}

	useEffect(async () => {
		try {
			const getContactResponse = await getContact()
			if (!getContactResponse) {
				await createContact()
			}
		} catch (error) {
			dispatch({ type: "SET_ERROR", error: error.message })
		}
	}, [])

	useEffect(() => {
		dispatch({ type: "SET_LOADING", isLoading: true })
		getAllSurveyQuestions()

		return () => {
			dispatch({ type: "SET_LOADING", isLoading: false })
		}
	}, [])

	return { ...state, setResponse, createResponse, checkRequiredAnswer }
}
